/**
 * THE single source of truth for supported locales.
 *
 * Adding a language = ONE entry in `LOCALES` below + a matching
 * `public/locales/<code>/translation.json` file. Everything else (i18n config,
 * route guard, language switcher, SEO hreflang, number formatting) reads from
 * here — no parallel `['da','en']` lists, no `x === 'da' ? … : …` ternaries.
 *
 * Danish is the default: Helin is a Danish salon chain (helinhair.dk), so `da`
 * is the routing fallback, the i18next fallback language, the SEO x-default,
 * and the one structurally-required key on `LocalisedString`.
 *
 * The key-parity CI test (src/test/i18n-key-parity.test.ts) enforces that every
 * locale's translation.json has the same keys as the default, so a half-added
 * locale fails the build instead of silently rendering the fallback.
 */

import i18next from 'i18next'

export interface LocaleMeta {
  /** Native label shown in the language switcher. */
  label: string
  /**
   * Path to the locale's flag image (served from /public). Small optimised
   * WebP, ~14px tall. Image (not emoji) so it renders identically on every OS,
   * including Windows where flag emoji fall back to the bare country code.
   */
  flag: string
  /** Value for the <html lang> attribute. */
  htmlLang: string
  /** Open Graph og:locale, e.g. "da_DK". */
  ogLocale: string
  /** BCP-47 tag for Intl.NumberFormat / Intl.DateTimeFormat, e.g. "da-DK". */
  intl: string
  /**
   * BCP-47 tag for JSON-LD `inLanguage` / SEO audience, e.g. "da-DK".
   * Distinct from `intl`: en prices format as da-DK (krone grouping) but the
   * English content addresses an en-US audience.
   */
  ld: string
  /** Text direction. */
  dir: 'ltr' | 'rtl'
}

// Order matters: index 0 is the default locale. Danish first, then English and
// German (the requested priority order), then the remaining locales.
//
// `intl` is pinned to each locale's *-DK form so DKK prices keep Danish krone
// grouping (the salon is in Denmark, all prices are in kroner) while the rest of
// the formatting (decimal/grouping separators) follows the locale. `ld` reflects
// the *content audience* for SEO/JSON-LD inLanguage, which is the locale's home
// region.
export const LOCALES = {
  da: { label: 'Dansk', flag: '/images/flags/da.webp', htmlLang: 'da', ogLocale: 'da_DK', intl: 'da-DK', ld: 'da-DK', dir: 'ltr' },
  en: { label: 'English', flag: '/images/flags/en.webp', htmlLang: 'en', ogLocale: 'en_US', intl: 'en-DK', ld: 'en-US', dir: 'ltr' },
  de: { label: 'Deutsch', flag: '/images/flags/de.webp', htmlLang: 'de', ogLocale: 'de_DE', intl: 'de-DK', ld: 'de-DE', dir: 'ltr' },
  sv: { label: 'Svenska', flag: '/images/flags/sv.webp', htmlLang: 'sv', ogLocale: 'sv_SE', intl: 'sv-DK', ld: 'sv-SE', dir: 'ltr' },
  nb: { label: 'Norsk', flag: '/images/flags/nb.webp', htmlLang: 'nb', ogLocale: 'nb_NO', intl: 'nb-DK', ld: 'nb-NO', dir: 'ltr' },
  fr: { label: 'Français', flag: '/images/flags/fr.webp', htmlLang: 'fr', ogLocale: 'fr_FR', intl: 'fr-DK', ld: 'fr-FR', dir: 'ltr' },
  es: { label: 'Español', flag: '/images/flags/es.webp', htmlLang: 'es', ogLocale: 'es_ES', intl: 'es-DK', ld: 'es-ES', dir: 'ltr' },
  pt: { label: 'Português', flag: '/images/flags/pt.webp', htmlLang: 'pt', ogLocale: 'pt_PT', intl: 'pt-DK', ld: 'pt-PT', dir: 'ltr' },
  nl: { label: 'Nederlands', flag: '/images/flags/nl.webp', htmlLang: 'nl', ogLocale: 'nl_NL', intl: 'nl-DK', ld: 'nl-NL', dir: 'ltr' },
  pl: { label: 'Polski', flag: '/images/flags/pl.webp', htmlLang: 'pl', ogLocale: 'pl_PL', intl: 'pl-DK', ld: 'pl-PL', dir: 'ltr' },
  it: { label: 'Italiano', flag: '/images/flags/it.webp', htmlLang: 'it', ogLocale: 'it_IT', intl: 'it-DK', ld: 'it-IT', dir: 'ltr' },
} as const satisfies Record<string, LocaleMeta>

/** Union of every supported locale code, derived from LOCALES. */
export type Lang = keyof typeof LOCALES

/** Ordered list of supported locale codes; index 0 is the default. */
export const SUPPORTED = Object.keys(LOCALES) as Lang[]

/**
 * Fallback locale used for redirects, i18next fallback, and SEO x-default.
 * Typed as the literal `'da'` (not widened to `Lang`) so `LocalisedString` can
 * require exactly this key — see that type below.
 */
export const DEFAULT_LANG = 'da' satisfies Lang

/** Type guard: true only for a declared locale code (case-sensitive). */
export function isLang(value: string): value is Lang {
  return Object.prototype.hasOwnProperty.call(LOCALES, value)
}

/** Metadata for a known locale. */
export function localeMeta(lang: Lang): LocaleMeta {
  return LOCALES[lang]
}

/**
 * Narrows a raw URL `:lang` param (string | undefined) to a supported Lang,
 * falling back to the default. Replaces the repeated
 * `(lang === 'da' ? 'da' : 'en') as 'en' | 'da'` idiom across pages.
 */
export function toLang(value: string | undefined): Lang {
  return value && isLang(value) ? value : DEFAULT_LANG
}

/**
 * A piece of content with translations per locale.
 *
 * Only the DEFAULT locale (`da`) is structurally required; every other locale is
 * optional. This is a deliberate change from the original "every Lang required"
 * model: when we scaled from 2 → 11 locales, requiring all 11 on every one of
 * the ~240 config content objects would have made the build red until all 11 were
 * authored, blocking incremental rollout. With `da` required + the rest optional:
 *
 *   - Danish (the brand's primary language) can never be missing — caught at
 *     compile time, same loud failure as before for the language that matters most.
 *   - A missing non-default translation falls back to Danish at runtime via
 *     `loc()` (and the `value[lang] ?? value[DEFAULT_LANG]` pattern), so nothing
 *     ever renders blank.
 *
 * Direct `value[lang]` indexing is now `string | undefined` for non-default
 * locales; prefer `loc(value, lang)` at render sites, which always returns a
 * `string`.
 */
export type LocalisedString = Record<typeof DEFAULT_LANG, string> &
  Partial<Record<Lang, string>>

/**
 * A fully-qualified i18next key string, e.g. 'salons:trustBadges.0.title'.
 * Config modules store these instead of inline LocalisedString objects; the
 * actual copy lives in public/locales/<lang>/<ns>.json. Alias of string for
 * readability — src/test/config-key-integrity.test.ts verifies every such
 * literal resolves against the default locale's resources.
 */
export type I18nKey = string

/**
 * Resolves a LocalisedString for the active language.
 *
 * Fallback chain: requested locale → English → Danish (the structurally
 * required default). English sits ahead of Danish because for the 9 non-Nordic
 * locales (de/fr/es/…) an English string is the more legible stop-gap than
 * Danish while a translation is still being authored. `value[DEFAULT_LANG]` is
 * always present at the type level, so this never returns undefined.
 *
 * Key strings: also accepts an I18nKey ('ns:dotted.path' — config content
 * lives in per-locale JSON namespaces) and resolves it through i18next for
 * `lang`. In the browser and in tests that's the global singleton (initialised
 * by src/i18n.ts / src/test/setup.ts); the SSG prerender renders through
 * isolated createInstance() instances instead, so src/i18n.server.ts registers
 * its instance via setLocI18n() before each render.
 */
export function loc(value: LocalisedString | I18nKey, lang: Lang): string {
  if (typeof value === 'string') {
    return locI18n.getFixedT(lang)(value)
  }
  return value[lang] ?? value.en ?? value[DEFAULT_LANG]
}

type LocResolver = Pick<typeof i18next, 'getFixedT'>

let locI18n: LocResolver = i18next

/**
 * Points loc()'s key-string resolution at a specific i18next instance. Only
 * the prerender needs this (its per-locale instances are isolated from the
 * global singleton); the prerender loop is sequential, so a single mutable
 * slot is race-free.
 */
export function setLocI18n(instance: LocResolver): void {
  locI18n = instance
}
